import { useCallback } from "react";
import styled from "styled-components";
import {
  AdjustmentsHorizontalIcon,
  CheckIcon,
  InformationCircleIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import { External } from "@sablier/v2-components/atoms";
import { Action, Label, SwitchBox } from "@sablier/v2-components/molecules";
import { links } from "@sablier/v2-constants";
import { Trans, useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { useSessionApproval } from "~/client/hooks";

const WrapperPartial = styled.div`
  flex: 1;
  flex-shrink: 0;
  width: 100%;
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
    padding: ${(props) => props.theme.sizes.edge};
    padding-inline: ${(props) => props.theme.sizes.modalEdge};
  }
`;

const List = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
    & > p {
      ${(props) => props.theme.styles.textParagraph}
      & {
        color: ${(props) => props.theme.colors.gray200};
        line-height: 150%;
      }
    }
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.theme.colors.border};
`;

const Description = styled.div`
  & > p {
    ${(props) => props.theme.styles.textParagraph}
    & {
      color: ${(props) => props.theme.colors.gray200};
      line-height: 20pt;

      a {
        text-decoration: underline;
      }
    }
  }
`;

const Box = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    padding-left: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    border: 2px solid ${(props) => props.theme.colors.border};
    border-radius: 8px;
    background-color: ${(props) => props.theme.colors.dark050};
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    ${Content} {
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    ${Content} {
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      ${List} {
        row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      }
    }
  }
`;

function Body() {
  const { approval, setApproval } = useSessionApproval();
  const { t } = useT();

  const onChange = useCallback(() => {
    setApproval(approval === "permit2" ? "approve" : "permit2");
  }, [approval, setApproval]);

  return (
    <Wrapper>
      <Content>
        <Label
          value={_.capitalize(t("words.about"))}
          icon={InformationCircleIcon}
        />
        <Description>
          <p>
            <Trans
              i18nKey={"modalCards.approval.description"}
              components={[
                <External key={"external"} to={links.docs.approve} />,
                <External key={"external"} to={links.docs.permit2} />,
              ]}
            />
          </p>
        </Description>
        <Label
          value={_.capitalize(t("words.recommendations"))}
          icon={StarIcon}
        />
        <List>
          <p>
            <b>{_.capitalize(t("words.permit2"))}:</b>{" "}
            {t("modalCards.approval.recommended.permit2")}
          </p>
          <p>
            <b>{t("structs.erc20Approval")}:</b>{" "}
            {t("modalCards.approval.recommended.approve")}
          </p>
        </List>
        <Divider />
        <Label
          value={_.capitalize(t("structs.toggleDefault"))}
          icon={AdjustmentsHorizontalIcon}
        />
        <Box>
          <Action title={t("modalCards.approval.action.title")}>
            <SwitchBox
              iconOff={CheckIcon}
              id={"approval"}
              isChecked={approval === "permit2"}
              labelOn={t("words.permit2")}
              labelOff={t("structs.erc20Approval")}
              onChange={onChange}
            />
          </Action>
        </Box>
      </Content>
    </Wrapper>
  );
}

export default Body;
